import { useTranslations } from '@vocab/react';
import { Button, useToast } from 'braid-design-system';
import React, { type FunctionComponent, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { EVENT_NAME_INTERACTION } from 'src/app/analytics';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { ANALYTICS_SAVE_DRAFT } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { sendLinkEvent } from 'src/app/utils/analytics';
import parseValidationResult from 'src/app/utils/parseValidationResult';
import { useDraft } from 'src/app/views/hooks/useDraft';
import type AppState from 'src/types/appState';
import type { ValidationResultType } from 'src/types/validation';

import translations from './.vocab';

const ANALYTICS_LINK_NAME_SAVE_DRAFT = 'job-ad-saved';

interface Props {
  handleValidation: (
    skipIfNull?: boolean,
  ) => Promise<ValidationResultType> | ValidationResultType;
  handleSaveDraftFailed: () => void;
}

export const SaveDraftButton: FunctionComponent<Props> = ({
  handleValidation,
  handleSaveDraftFailed,
}) => {
  const { tealium } = useSelector(
    (state: AppState) => ({
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);
  const [isSaving, setIsSaving] = useState(false);
  const showToast = useToast();
  const { saveDraft } = useDraft();

  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const handleClick = async () => {
    const validationResult = await handleValidation(true);
    const { isValid } = parseValidationResult(validationResult);

    if (isValid) {
      setIsSaving(true);

      // TODO: Remove else once we start using new analytics implementation
      if (isPageAnalyticsEnabled) {
        getTrackingAdapter.sendLinkEvent({
          eventName: ANALYTICS_SAVE_DRAFT,
        });
      } else {
        const eventData = {
          linkName: ANALYTICS_LINK_NAME_SAVE_DRAFT,
          eventName: [ANALYTICS_LINK_NAME_SAVE_DRAFT, EVENT_NAME_INTERACTION],
        };
        sendLinkEvent(tealium, eventData);
      }

      try {
        await saveDraft(false);
        setIsSaving(false);

        showToast({
          message: t('save-draft-button-success-message'),
          tone: 'positive',
        });
      } catch (error) {
        setIsSaving(false);
        handleSaveDraftFailed();
      }
    }
  };

  return (
    <Button
      variant="transparent"
      onClick={handleClick}
      data={{ testid: 'saveDraftButton' }}
      loading={isSaving}
    >
      {t('save-draft-button-text')}
    </Button>
  );
};
